import { useEffect, useState } from 'react'
import { useAuthStore } from '../../auth/authStore'
import { BrokerSelect } from '../brokers/BrokerSelect'
import { readLastBroker, saveLastBroker, useCustomsBrokers } from '../brokers/useCustomsBrokers'
import { pluralPositions } from '../customsSelection'

const NOTE_MAX = 1000

interface Props {
  positions: number
  uniqueParts: number
  /** Сколько выбранных позиций уже ждут ответа по другому запросу. */
  alreadySent: number
  busy: boolean
  onClose: () => void
  onSubmit: (params: { brokerId: string | null; note: string }) => void
}

/**
 * «Отправить брокеру»: кому уходит файл (карточка брокера — при нескольких
 * брокерах видно, кому какой запрос ушёл) и примечание к запросу. По умолчанию —
 * брокер прошлой отправки, а если брокер в справочнике один — он.
 */
export function SendToBrokerModal({
  positions,
  uniqueParts,
  alreadySent,
  busy,
  onClose,
  onSubmit,
}: Props) {
  const userId = useAuthStore((s) => s.user?.id)
  const brokers = useCustomsBrokers()
  const [brokerId, setBrokerId] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  const [note, setNote] = useState('')

  // Умолчание — когда справочник загрузился и логист ещё ничего не выбрал сам.
  useEffect(() => {
    if (touched || !brokers.data) return
    const last = readLastBroker(userId)
    const fallback = brokers.data.length === 1 ? brokers.data[0]!.id : null
    setBrokerId(last && brokers.data.some((b) => b.id === last) ? last : fallback)
  }, [brokers.data, touched, userId])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, busy])

  const submit = () => {
    if (busy) return
    saveLastBroker(userId, brokerId)
    onSubmit({ brokerId, note: note.trim() })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label="Отправить брокеру"
        className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md mx-4"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Отправить брокеру</h3>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
          >
            ×
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-700">
            {pluralPositions(positions)}, уникальных партномеров: {uniqueParts}. Позиции получат
            статус «Отправлено брокеру», файл Excel скачается сразу.
            {alreadySent > 0 &&
              ` Уже отправлены раньше: ${alreadySent} — попадут и в новый запрос.`}
          </p>
          <div>
            <label className="block text-xs text-gray-500 mb-1" htmlFor="send-broker-select">
              Брокер
            </label>
            <BrokerSelect
              id="send-broker-select"
              value={brokerId}
              disabled={busy}
              onChange={(v) => {
                setTouched(true)
                setBrokerId(v)
              }}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1" htmlFor="send-broker-note">
              Примечание к запросу
            </label>
            <textarea
              id="send-broker-note"
              value={note}
              maxLength={NOTE_MAX}
              rows={2}
              disabled={busy}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Необязательно: срочно, повторный запрос…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
            />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-1.5 border border-gray-300 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
          >
            {busy ? 'Отправка…' : 'Отправить и скачать файл'}
          </button>
        </div>
      </div>
    </div>
  )
}
